import { useIntl } from 'react-intl';
import { LoadingSkeleton, Tag, useQueryFetch } from 'sonata-react-components';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { getLineItemDays, getLineItemToday } from './lineItemDays.helper';

import type { MultiFlightCalendarInfo } from 'sonata-static-storage/lib/common/common.types';

type Props = {
  countryCode?: string;
  entityId: string;
  getCalendarInfo: () => Promise<MultiFlightCalendarInfo>;
  queryKey: string[];
  timezone?: string;
};

const CALENDAR_INFO_CACHE_MS = 5 * 60 * 1000;

/** "Current / Total days" cell of the line items list, in the same format as the pacing status modal. */
export function LineItemDays({ countryCode, entityId, getCalendarInfo, queryKey, timezone }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { data, isFetching } = useQueryFetch(['useGetCalendarInfo', entityId, ...queryKey], getCalendarInfo, {
    cacheTime: CALENDAR_INFO_CACHE_MS,
    retry: 0
  });

  if (isFetching) {
    return <LoadingSkeleton count={1} />;
  }

  if (!data?.availableDates.length) {
    return <span className="lineItemDays">-</span>;
  }

  const { currentDays, daysPct, totalDays } = getLineItemDays(data.availableDates, getLineItemToday(timezone));

  return (
    <div className="lineItemDays">
      <span>{currentDays}</span>
      <span>/</span>
      <span className="lineItemDays__total">
        {formatMessage({ defaultMessage: '{totalDays} days', id: 'lineItems.list.days.total' }, { totalDays })}
      </span>
      <Tag id={`lineItemDays-${entityId}`}>{`${normalize(daysPct, 2, countryCode)}%`}</Tag>
    </div>
  );
}
